import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/feature/AppLayout";
import SummaryCard from "@/components/feature/SummaryCard";
import ExpenseChart, { type BarDatum } from "@/components/feature/ExpenseChart";
import CategoryChart, { type DonutDatum } from "@/components/feature/CategoryChart";
import CategoryBadge from "@/components/feature/CategoryBadge";
import type { Expense, Category, AppSettings } from "@/models/types";
import { useToast } from "@/store/ToastContext";
import { computeTotals, computeCategoryTotals } from "@/services/api";
import { formatCurrency } from "@/utils/currency";
import { formatShortDate } from "@/utils/date";
import { paymentIcon } from "@/utils/format";
import { expenseApi, categoryApi, settingsApi } from "@/services/api";

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function buildDailyData(expenses: Expense[]): BarDatum[] {
  const now = new Date();
  const startMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const map = new Map<string, number>();
  expenses.forEach((e) => {
    const d = new Date(e.date);
    if (d >= startMonth && d <= now) {
      const key = e.date.slice(0, 10);
      map.set(key, (map.get(key) || 0) + e.amount);
    }
  });
  const days: BarDatum[] = [];
  for (let i = 1; i <= now.getDate(); i++) {
    const key = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(i)}`;
    days.push({ label: String(i), total: map.get(key) || 0 });
  }
  return days;
}

export default function Dashboard() {
  const { showToast } = useToast();

  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [expensesData, categoriesData, settingsData] = await Promise.all([
          expenseApi.list(),
          categoryApi.list(),
          settingsApi.get(),
        ]);
        // Mapear campos snake_case a camelCase si es necesario
        const mappedExpenses = expensesData.map((e: any) => ({
          ...e,
          categoryId: e.category_id ?? e.categoryId,
          paymentMethod: e.payment_method ?? e.paymentMethod,
          createdAt: e.created_at ?? e.createdAt,
          receipt: e.receipt ?? null,
        }));
        setExpenses(mappedExpenses);
        setCategories(categoriesData);
        setSettings(settingsData);
      } catch (error) {
        console.error("Error cargando dashboard:", error);
        showToast("Error al cargar los datos.", "error");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [showToast]);

  if (loading || !settings) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
        </div>
      </AppLayout>
    );
  }

  const currency = settings.currency;
  const totals = computeTotals(expenses);
  const budget = settings.monthlyBudget;
  const budgetPct =
    budget > 0 ? Math.min(100, Math.round((totals.monthTotal / budget) * 100)) : 0;
  const diff = totals.monthTotal - totals.prevMonthTotal;
  const diffPct =
    totals.prevMonthTotal > 0
      ? Math.round((Math.abs(diff) / totals.prevMonthTotal) * 100)
      : 0;
  const diffUp = diff >= 0;

  const dailyData = buildDailyData(expenses);
  const catData: DonutDatum[] = computeCategoryTotals(expenses, categories)
    .filter((c) => c.total > 0)
    .map((c) => ({ name: c.category.name, value: c.total, color: c.category.color }));

  const recent = [...expenses]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 6);

  return (
    <AppLayout>
      {/* Cabecera */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
            Hola, {settings.userName.split(" ")[0]} 👋
          </h2>
          <p className="text-sm text-foreground-500 mt-1">
            Este es tu resumen financiero de hoy.
          </p>
        </div>
        <Link
          to="/gastos/nuevo"
          className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 text-white px-5 py-2.5 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line text-lg" /> Nuevo gasto
        </Link>
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <SummaryCard
          title="Total gastado este mes"
          value={formatCurrency(totals.monthTotal, currency)}
          icon="ri-wallet-3-line"
          tone="primary"
        />
        <SummaryCard
          title="Total esta semana"
          value={formatCurrency(totals.weekTotal, currency)}
          icon="ri-calendar-line"
          tone="secondary"
        />
        <SummaryCard
          title="Gastos registrados"
          value={String(totals.countThisMonth)}
          icon="ri-file-list-3-line"
          tone="accent"
          sub="en este mes"
        />
        <SummaryCard
          title="Promedio de gasto diario"
          value={formatCurrency(totals.dailyAverage, currency)}
          icon="ri-bar-chart-line"
          tone="secondary"
        />
      </div>

      {/* Presupuesto y comparación */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4 mt-4">
        <div className="bg-background-50 border border-background-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-medium text-foreground-700">
              Presupuesto mensual
            </p>
            <span className="text-xs font-semibold text-foreground-500">
              {formatCurrency(budget, currency)}
            </span>
          </div>
          <div className="flex items-end justify-between mb-2">
            <p className="text-2xl font-heading font-bold text-foreground-950">
              {formatCurrency(totals.monthTotal, currency)}
            </p>
            <p className="text-sm font-semibold text-foreground-700">
              {budgetPct}% utilizado
            </p>
          </div>
          <div className="h-3 w-full bg-background-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                budgetPct > 90 ? "bg-red-500" : budgetPct > 70 ? "bg-accent-500" : "bg-primary-500"
              }`}
              style={{ width: `${budgetPct}%` }}
            />
          </div>
          <p className="mt-2 text-xs text-foreground-500">
            {budget > 0
              ? `Te quedan ${formatCurrency(Math.max(0, budget - totals.monthTotal), currency)} de tu presupuesto.`
              : "Configura un presupuesto mensual en Ajustes."}
          </p>
        </div>

        <div className="bg-background-50 border border-background-200 rounded-lg p-5">
          <p className="text-sm font-medium text-foreground-700 mb-3">
            Comparación con el mes anterior
          </p>
          <div className="flex items-center gap-3">
            <span
              className={`w-12 h-12 rounded-full flex items-center justify-center ${
                diffUp ? "bg-primary-100 text-primary-600" : "bg-red-100 text-red-500"
              }`}
            >
              <i className={`${diffUp ? "ri-arrow-up-line" : "ri-arrow-down-line"} text-2xl`} />
            </span>
            <div>
              <p className="text-2xl font-heading font-bold text-foreground-950">
                {diffUp ? "+" : "−"}
                {formatCurrency(Math.abs(diff), currency)}
              </p>
              <p className="text-sm text-foreground-500">
                {diffPct}% {diffUp ? "más" : "menos"} que el mes pasado
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <div className="lg:col-span-2 bg-background-50 border border-background-200 rounded-lg p-5">
          <h3 className="text-base font-heading font-semibold text-foreground-950 mb-4">
            Gastos por día
          </h3>
          <ExpenseChart data={dailyData} currencyCode={currency} />
        </div>
        <div className="bg-background-50 border border-background-200 rounded-lg p-5">
          <h3 className="text-base font-heading font-semibold text-foreground-950 mb-4">
            Distribución por categoría
          </h3>
          {catData.length > 0 ? (
            <CategoryChart data={catData} currencyCode={currency} />
          ) : (
            <p className="text-sm text-foreground-500 py-10 text-center">
              Sin datos todavía.
            </p>
          )}
        </div>
      </div>

      {/* Gastos recientes */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-heading font-semibold text-foreground-950">
            Gastos recientes
          </h3>
          <Link
            to="/gastos"
            className="text-sm font-medium text-primary-600 hover:text-primary-700 cursor-pointer"
          >
            Ver todos
          </Link>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-lg divide-y divide-background-200">
          {recent.length > 0 ? (
            recent.map((e) => (
              <Link
                key={e.id}
                to={`/gastos/${e.id}`}
                className="flex items-center gap-3 p-3 md:px-4 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <CategoryBadge categoryId={e.categoryId} showName={false} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground-900 truncate">
                    {e.description}
                  </p>
                  <p className="text-xs text-foreground-500">{formatShortDate(e.date)}</p>
                </div>
                <span className="hidden sm:inline-flex items-center gap-1 text-foreground-500">
                  <i className={paymentIcon(e.paymentMethod)} />
                </span>
                {e.receipt && (
                  <span className="w-7 h-7 flex items-center justify-center rounded-md bg-primary-100 text-primary-600">
                    <i className="ri-receipt-line" />
                  </span>
                )}
                <p className="font-semibold text-foreground-950 whitespace-nowrap">
                  {formatCurrency(e.amount, currency)}
                </p>
              </Link>
            ))
          ) : (
            <p className="text-sm text-foreground-500 p-4 text-center">
              No hay gastos recientes.
            </p>
          )}
        </div>
      </div>
    </AppLayout>
  );
}